import { StrictMode, useCallback, useState } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { ApiError } from "./api";
import { I18nContext, type Lang, detectLang, loadLang } from "./i18n";
import { router } from "./router";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2,
      refetchOnWindowFocus: false,
    },
  },
});

function App({ initial }: { initial: Lang }) {
  const [lang, setLangState] = useState<Lang>(initial);
  // A language switch waits for that language's strings (one small chunk).
  const setLang = useCallback((l: Lang) => {
    try { localStorage.setItem("lumi_ui", l); } catch { /* storage blocked */ }
    loadLang(l).then(() => setLangState(l), () => undefined);
  }, []);
  return (
    <I18nContext.Provider value={{ lang, setLang }}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </I18nContext.Provider>
  );
}

const initial = detectLang();
// Render once the first language is in; if its chunk cannot load, render anyway (English, else the keys).
void loadLang(initial)
  .catch(() => loadLang("en"))
  .catch(() => undefined)
  .finally(() => createRoot(document.getElementById("root")!).render(<StrictMode><App initial={initial} /></StrictMode>));
